import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { useLocalizeMessage } from 'libs.nucleus';
import { overlayPercent, readOverlayKey, nextOverlayPage, sameOverlayPosition } from './overlay_keyboard';
import { clampOverlayResize, CONSENT_ELEMENT_TYPES, overlayResizeMin, } from '../../constants/consent_element_types.constants';
export const ConsentElementMarker = ({ element, isSelected, isTabStop, totalPages, onSelect, onUpdate, onUpdatePosition, onDelete, onConfigureClick, onAnnounce, }) => {
    const translate = useLocalizeMessage();
    const { widthPct: MIN_WIDTH_PCT, heightPct: MIN_HEIGHT_PCT } = overlayResizeMin(element.type);
    const min = { widthPct: MIN_WIDTH_PCT, heightPct: MIN_HEIGHT_PCT };
    const def = CONSENT_ELEMENT_TYPES[element.type];
    const containerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef(null);
    const name = element.config.ec__label?.trim();
    const accessibleName = name ? `${def.label}: ${name}` : def.label;
    const announceMove = (position) => onAnnounce(translate('Moved to {x} percent from the left, {y} percent from the top', {
        x: overlayPercent(position.xPct),
        y: overlayPercent(position.yPct),
    }));
    const announceResize = (position) => onAnnounce(translate('Resized to {width} percent wide by {height} percent tall', {
        width: overlayPercent(position.widthPct),
        height: overlayPercent(position.heightPct),
    }));
    const handleMouseDown = (e) => {
        if (e.target.closest('[data-action]')) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        onSelect(element.id);
        containerRef.current?.focus({ preventScroll: true });
        const parent = containerRef.current?.parentElement?.parentElement;
        if (!parent) {
            return;
        }
        const parentRect = parent.getBoundingClientRect();
        dragStartRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            pos: { ...element.position },
        };
        setIsDragging(true);
        const onMouseMove = (moveEvent) => {
            if (!dragStartRef.current) {
                return;
            }
            const dx = (moveEvent.clientX - dragStartRef.current.mouseX) / parentRect.width;
            const dy = (moveEvent.clientY - dragStartRef.current.mouseY) / parentRect.height;
            const newX = Math.max(0, Math.min(1 - element.position.widthPct, dragStartRef.current.pos.xPct + dx));
            const newY = Math.max(0, Math.min(1 - element.position.heightPct, dragStartRef.current.pos.yPct + dy));
            onUpdatePosition(element.id, { xPct: newX, yPct: newY });
        };
        const onMouseUp = () => {
            setIsDragging(false);
            dragStartRef.current = null;
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };
    const handleResizeMouseDown = (e, resizeMode = 'both') => {
        e.preventDefault();
        e.stopPropagation();
        // preventDefault keeps focus where it was, so a page typed into the inspector would stay an
        // uncommitted draft while the resize saves the overlay on its old page. Focusing the marker, as
        // a drag does, blurs that field and commits the page first.
        containerRef.current?.focus({ preventScroll: true });
        const parent = containerRef.current?.parentElement?.parentElement;
        if (!parent) {
            return;
        }
        const parentRect = parent.getBoundingClientRect();
        const startPos = { ...element.position };
        const startX = e.clientX;
        const startY = e.clientY;
        const onMouseMove = (moveEvent) => {
            const dw = (moveEvent.clientX - startX) / parentRect.width;
            const dh = (moveEvent.clientY - startY) / parentRect.height;
            const nextWidth = resizeMode === 'height' ? startPos.widthPct : startPos.widthPct + dw;
            const nextHeight = resizeMode === 'width' ? startPos.heightPct : startPos.heightPct + dh;
            const { widthPct, heightPct } = clampOverlayResize(startPos, nextWidth, nextHeight, min);
            onUpdatePosition(element.id, { widthPct, heightPct });
        };
        const onMouseUp = () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };
    const handleKeyDown = (e) => {
        const result = readOverlayKey(e.key, e, element.position, min);
        if (!result) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (result.kind === 'select') {
            onConfigureClick(element);
            return;
        }
        if (result.kind === 'delete') {
            onAnnounce(translate('{label} removed', { label: accessibleName }));
            onDelete(element.id);
            return;
        }
        if (result.kind === 'page') {
            const page = nextOverlayPage(element.page, result.delta, totalPages);
            if (page === null) {
                onAnnounce(result.delta < 0 ? translate('Already on the first page') : translate('Already on the last page'));
                return;
            }
            onUpdate({ ...element, page });
            onAnnounce(translate('Moved to page {page}', { page }));
            return;
        }
        if (sameOverlayPosition(element.position, result.position)) {
            onAnnounce(result.kind === 'resize' ? translate('Cannot resize further') : translate('Cannot move further'));
            return;
        }
        onUpdate({ ...element, position: result.position });
        if (result.kind === 'nudge') {
            announceMove(result.position);
        }
        else {
            announceResize(result.position);
        }
    };
    return (_jsxs("div", { ref: containerRef, role: 'group', tabIndex: isTabStop ? 0 : -1, "aria-label": accessibleName, "data-overlay-item": `el-${element.id}`, "data-testid": `consent-element-marker-${element.id}`, style: {
            position: 'absolute',
            left: `${element.position.xPct * 100}%`,
            top: `${element.position.yPct * 100}%`,
            width: `${element.position.widthPct * 100}%`,
            height: `${element.position.heightPct * 100}%`,
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            overflow: 'visible',
            // Inset: offset 2 paints outside the page and is clipped at y = 0.
            outline: element.needsDimensionConfirmation
                ? '2px solid #d97706'
                : isSelected
                    ? `2px solid ${def.color}`
                    : undefined,
            outlineOffset: element.needsDimensionConfirmation || isSelected ? -2 : undefined,
        }, onMouseDown: handleMouseDown, onFocus: () => onSelect(element.id), onKeyDown: handleKeyDown, children: [_jsxs("div", { style: {
                    borderColor: def.color,
                    backgroundColor: isSelected ? def.color : `${def.color}14`,
                }, className: 'relative flex h-full w-full items-center gap-1.5 overflow-hidden rounded border-2 px-2', children: [_jsx("span", { className: `min-w-0 flex-1 truncate text-[10.5px] font-medium leading-[13px] ${isSelected ? 'text-white' : 'text-neutral-900'}`, "data-testid": `element-header-${element.id}`, children: element.config.ec__label || def.label }), _jsx("button", { type: 'button', tabIndex: -1, "data-action": 'configure', "data-testid": `configure-element-${element.id}`, className: 'nucleus-sr-only', "aria-label": translate('Configure'), onClick: (e) => {
                            e.stopPropagation();
                            onConfigureClick(element);
                        }, children: translate('Configure') }), _jsx("button", { type: 'button', tabIndex: -1, "data-action": 'delete', "data-testid": `delete-element-${element.id}`, className: `border-0 bg-transparent p-0 text-[10px] ${isSelected ? 'text-white' : 'text-neutral-900/60'}`, "aria-label": translate('Delete'), onClick: (e) => {
                            e.stopPropagation();
                            onDelete(element.id);
                        }, title: translate('Delete'), children: "\u2715" })] }), _jsx("button", { type: 'button', tabIndex: -1, "data-action": 'resize', "data-testid": `resize-handle-width-${element.id}`, "aria-label": translate('Resize width'), style: {
                    position: 'absolute',
                    top: 0,
                    right: -4,
                    width: 8,
                    height: '100%',
                    cursor: 'ew-resize',
                }, onMouseDown: (e) => handleResizeMouseDown(e, 'width') }), _jsx("button", { type: 'button', tabIndex: -1, "data-action": 'resize', "data-testid": `resize-handle-height-${element.id}`, "aria-label": translate('Resize height'), style: {
                    position: 'absolute',
                    left: 0,
                    bottom: -4,
                    width: '100%',
                    height: 8,
                    cursor: 'ns-resize',
                }, onMouseDown: (e) => handleResizeMouseDown(e, 'height') }), _jsx("button", { type: 'button', tabIndex: -1, "data-action": 'resize', "data-testid": `resize-handle-${element.id}`, "aria-label": translate('Resize width and height'), style: {
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 10,
                    height: 10,
                    cursor: 'se-resize',
                    backgroundColor: def.color,
                    borderRadius: '2px 0 2px 0',
                }, onMouseDown: (e) => handleResizeMouseDown(e, 'both') })] }));
};
